import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel } from '@mui/material';
import { hasPermission } from '../../helpers/Permission';
import { PermissionTypes } from '../../constants';
import { API_CALL } from '../../configs/ActionTypes';
import { enqueueSnackbarCustomMessageHandler } from '../../actions/Status';
import { getSSOValidationError } from '../../helpers/SSOValidation';
import SSOUpload, { SSOFormHandle, FormStateType } from './SSOUploadUSB';

const styles = require('./styles.pcss');

const formatCertificateForEditing = (cert?: string): string => {
  const trimmed = cert?.trim() || '';
  if (!trimmed || trimmed.startsWith('-----BEGIN CERTIFICATE-----')) {
    return trimmed;
  }

  return `-----BEGIN CERTIFICATE-----\n${trimmed}\n-----END CERTIFICATE-----`;
};

type Props = {
  organizationId: string;
  dispatch: any;
};

type ContainerProps = {
  organizationId: string;
};

export const SSOIntegrationSetup: React.FC<Props> = ({ organizationId, dispatch }) => {
  const permitted = hasPermission('features', PermissionTypes.update);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [enableProvisioning, setEnableProvisioning] = useState<boolean>(false);
  const [enforceRedirect, setEnforceRedirect] = useState<boolean>(false);
  const [disableRequestedAuthnContext, setDisableRequestedAuthnContext] = useState<boolean>(false);
  const [ssoFormHandle, setSsoFormHandle] = useState<SSOFormHandle | null>(null);
  const [initialValues, setInitialValues] = useState<Partial<FormStateType> | undefined>(undefined);
  const [existingIntegrationId, setExistingIntegrationId] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<any[]>([]);

  const selectIntegration = useCallback((integration: any | null) => {
    const settings = integration?.settings || {};
    setExistingIntegrationId(integration?._id || integration?.id || null);
    setEnableProvisioning(Boolean(settings.enableProvisioning));
    setEnforceRedirect(Boolean(settings.enforceRedirect));
    setDisableRequestedAuthnContext(Boolean(settings.disableRequestedAuthnContext));
    setInitialValues(integration ? {
      entrypoint: settings.entryPoint,
      issuer: settings.issuer,
      idpDisplayName: settings.idpDisplayName,
      cert: formatCertificateForEditing(settings.cert),
      statusBeforeDeactivation: settings.statusBeforeDeactivation,
      loginRedirectUrl: settings.loginRedirectUrl,
      provider: settings.provider,
      type: settings.type,
      samlIssuer: settings.samlIssuer,
      maxTtl: settings.maxTTL != null ? String(settings.maxTTL) : '',
      status: settings.status,
      enableProvisioning: settings.enableProvisioning,
      enforceRedirect: settings.enforceRedirect,
      disableRequestedAuthnContext: settings.disableRequestedAuthnContext,
    } : undefined);
  }, []);

  useEffect(() => {
    if (!permitted) return;
    dispatch({
      type: API_CALL,
      payload: {
        call: 'integrations/sso',
        method: 'get',
        data: { organizationId },
        success: (response: any) => {
          const payload = response?.payload;
          const loadedIntegrations = Array.isArray(payload)
            ? payload
            : payload
              ? [payload]
              : [];
          setIntegrations(loadedIntegrations);
          selectIntegration(loadedIntegrations[0] || null);
        },
        fail: () => {
          // No existing SSO config; form stays with default empty values
        },
      },
    });
  }, [dispatch, organizationId, permitted, selectIntegration]);

  const handleEnableProvisioning = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEnableProvisioning(e.target.checked),
    []
  );

  const handleEnforceRedirect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEnforceRedirect(e.target.checked),
    []
  );

  const handleDisableRequestedAuthnContext = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setDisableRequestedAuthnContext(e.target.checked),
    []
  );

  const handleSubmit = useCallback(() => {
    if (!ssoFormHandle) {
      enqueueSnackbarCustomMessageHandler(dispatch, 'Form initialization error', 'error');
      return;
    }

    const formData = ssoFormHandle.getFormData();
    const validationError = getSSOValidationError(
      formData.entrypoint,
      formData.issuer,
      formData.cert
    );
    
    if (!ssoFormHandle.validateAndMarkErrors()) {
      const errorMessage = validationError || 'Validation failed';
      enqueueSnackbarCustomMessageHandler(dispatch, errorMessage, 'error');
      return;
    }

    setIsLoading(true);

    const data = {
      organizationId,
      settings: {
        cert: formData.cert,
        entryPoint: formData.entrypoint,
        issuer: formData.issuer,
        enableProvisioning,
        idpDisplayName: formData.idpDisplayName,
        enforceRedirect,
        disableRequestedAuthnContext,
        maxTTL: parseInt(formData.maxTtl, 10),
        status: formData.status,
      },
    };

    const isUpdate = Boolean(existingIntegrationId);
    dispatch({
      type: API_CALL,
      payload: {
        call: isUpdate ? `integrations/${existingIntegrationId}` : 'integrations/sso',
        method: isUpdate ? 'put' : 'post',
        data: JSON.stringify(data),
        success: (response: any) => {
          setIsLoading(false);
          enqueueSnackbarCustomMessageHandler(
            dispatch,
            isUpdate ? 'Integration updated successfully!' : 'Integration added successfully!',
            'success'
          );
          const savedIntegration = response?.payload;
          const updatedId = savedIntegration?._id || savedIntegration?.id || existingIntegrationId;
          if (!isUpdate && savedIntegration) {
            setIntegrations(current => [...current, savedIntegration]);
          } else if (isUpdate && savedIntegration) {
            setIntegrations(current => current.map(integration => (
              (integration._id || integration.id) === updatedId ? savedIntegration : integration
            )));
          }
          if (savedIntegration) {
            selectIntegration(savedIntegration);
          } else {
            setExistingIntegrationId(updatedId);
          }
        },
        fail: (error: any) => {
          enqueueSnackbarCustomMessageHandler(dispatch, error?.payload?.message || 'API error occurred', 'error');
          setIsLoading(false);
        },
      },
    });
  }, [dispatch, organizationId, enableProvisioning, enforceRedirect, disableRequestedAuthnContext, ssoFormHandle, existingIntegrationId, selectIntegration]);

  if (!permitted) {
    return (
      <Paper className={styles.ssoIntegrationSetupCard}>
        <CardHeader className={styles.header} title="SAML Integration Manager" />
        <CardContent>
          <Typography color="error">
            Insufficient permissions. Contact your administrator to configure SSO integrations.
          </Typography>
        </CardContent>
      </Paper>
    );
  }

  return (
    <Paper className={styles.ssoIntegrationSetupCard}>
      <CardHeader className={styles.header} title="SAML Integration Manager" />
      <CardContent>
        {integrations.length > 0 && (
          <div className={styles.integrationList}>
            <Typography variant="subtitle1" className={styles.integrationListTitle}>
              SAML Integrations
            </Typography>
            {integrations.map((integration, index) => {
              const integrationId = integration._id || integration.id;
              const settings = integration.settings || {};
              const isSelected = integrationId === existingIntegrationId;
              return (
                <Button
                  key={integrationId || index}
                  variant={isSelected ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => selectIntegration(integration)}
                  className={styles.integrationListItem}
                >
                  {settings.idpDisplayName || `SAML Integration ${index + 1}`}
                </Button>
              );
            })}
            <Button
              variant="outlined"
              size="small"
              onClick={() => selectIntegration(null)}
              className={styles.addIntegrationButton}
            >
              Add SAML Integration
            </Button>
          </div>
        )}
        <SSOUpload
          key={existingIntegrationId || 'new-sso-integration'}
          onFormReady={setSsoFormHandle}
          initialValues={initialValues}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={enableProvisioning}
              onChange={handleEnableProvisioning}
              color="primary"
            />
          }
          label="Enable Provisioning"
          className={styles.toggle}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={enforceRedirect}
              onChange={handleEnforceRedirect}
              color="primary"
            />
          }
          label="Redirect on TravelBank login (This is off by default and will allow organizations to test their integration, once enabled all users whose email domains match the organization will be redirected to SSO"
          className={styles.toggle}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={disableRequestedAuthnContext}
              onChange={handleDisableRequestedAuthnContext}
              color="primary"
            />
          }
          label="Mobile Override"
          className={styles.toggle}
        />
        <div className={styles.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{ position: 'relative' }}
          >
            {isLoading
              ? (existingIntegrationId ? 'Updating Integration...' : 'Adding Integration...')
              : (existingIntegrationId ? 'Update Integration' : 'Add Integration')}
          </Button>
        </div>
      </CardContent>
    </Paper>
  );
};

const SSOIntegrationSetupContainer: React.FC<ContainerProps> = ({ organizationId }) => {
  const dispatch = useDispatch();

  return <SSOIntegrationSetup organizationId={organizationId} dispatch={dispatch} />;
};

export default SSOIntegrationSetupContainer;
